import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Download, CheckCircle, XCircle, Clock, User, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_videogist-3/artifacts/809w15is_summbee%20logo_just%20bee.png";

function BatchDetailPage() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchJobStatus();
  }, [jobId]);

  useEffect(() => {
    if (!autoRefresh || !job) return;
    if (job.status === "completed" || job.status === "failed") {
      setAutoRefresh(false);
      return;
    }

    const interval = setInterval(() => {
      fetchJobStatus();
    }, 3000); // Refresh every 3 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, job]);

  const fetchJobStatus = async () => {
    try {
      const response = await axios.get(`${API}/batch/${jobId}`, { withCredentials: true });
      setJob(response.data.job);
    } catch (error) {
      console.error("Error fetching job status:", error);
      toast.error("Failed to load job details");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadResults = () => {
    if (!job || !job.results) return;

    const data = job.results.map(r => ({
      video_url: r.video_url,
      video_title: r.video_title,
      platform: r.platform,
      transcription: r.transcription,
      bullet_summary: r.bullet_summary,
      structured_summary: r.structured_summary
    }));

    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `batch_results_${jobId}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Results downloaded");
  };

  if (loading || !job) {
    return (
      <div className="dashboard-page">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  const progress = job.total_videos > 0 ? (job.processed_videos / job.total_videos) * 100 : 0;
  const isActive = job.status === "pending" || job.status === "processing";

  return (
    <div className="dashboard-page">
      <nav className="navbar">
        <div className="nav-content">
          <div className="nav-logo" onClick={() => navigate("/")} style={{cursor: "pointer"}}>
            <img src={LOGO_URL} alt="SummBee" className="logo-image" />
            <span>SummBee</span>
          </div>
          <div className="nav-links">
            <Button onClick={() => navigate("/batch")} variant="ghost">
              <ArrowLeft size={16} /> Back to Batch
            </Button>
            <div className="user-info">
              <User size={16} />
              <span>{user?.name}</span>
            </div>
            <Button onClick={logout} variant="ghost" className="logout-button">
              <LogOut size={16} /> Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="dashboard-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Batch Job Details</h1>
            <p className="page-subtitle">Job ID: {jobId}</p>
          </div>
          {job.status === "completed" && (
            <Button onClick={handleDownloadResults} className="create-button">
              <Download size={20} /> Download Results
            </Button>
          )}
        </div>

        {/* Progress Card */}
        <Card className="batch-progress-card">
          <CardHeader>
            <div className="progress-header">
              <CardTitle>Progress</CardTitle>
              {isActive && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchJobStatus}
                  className="refresh-button"
                >
                  <RefreshCw size={16} />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="progress-stats">
              <div className="stat-item">
                <div className="stat-label">Status</div>
                <div className={`stat-value status-${job.status}`}>
                  {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-label">Processed</div>
                <div className="stat-value">{job.processed_videos} / {job.total_videos}</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">Successful</div>
                <div className="stat-value success">{job.successful_videos}</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">Failed</div>
                <div className="stat-value error">{job.failed_videos}</div>
              </div>
            </div>

            <div className="progress-bar-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="progress-text">{Math.round(progress)}%</div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {job.results && job.results.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                <CheckCircle className="card-icon success" />
                Successful Videos ({job.results.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="results-list">
                {job.results.map((result, idx) => (
                  <div key={idx} className="result-item">
                    <CheckCircle className="result-icon success" size={20} />
                    <div className="result-details">
                      <div className="result-title">{result.video_title}</div>
                      <div className="result-url">{result.video_url}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Errors */}
        {job.errors && job.errors.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                <XCircle className="card-icon error" />
                Failed Videos ({job.errors.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="errors-list">
                {job.errors.map((error, idx) => (
                  <div key={idx} className="error-item">
                    <XCircle className="result-icon error" size={20} />
                    <div className="error-details">
                      <div className="error-url">{error.video_url}</div>
                      <div className="error-message">{error.error}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {isActive && (
          <div className="auto-refresh-notice">
            <Clock size={16} />
            <span>Auto-refreshing every 3 seconds...</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default BatchDetailPage;
